import React from 'react';
import { Typography, Tag, Collapse } from 'antd';
import {
  UserOutlined,
  RobotOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;

export interface ToolCallInfo {
  name: string;
  status: 'running' | 'done' | 'error';
  count?: number;
  message?: string;
}

export interface ChatMessageData {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ToolCallInfo[];
  timestamp: Date;
}

const TOOL_LABELS: Record<string, string> = {
  lookup_units: 'Units',
  lookup_cost_centers: 'Cost centers',
  search_catalog_parts: 'Parts catalog',
  create_mr_draft: 'Create MR draft',
  search_deals: 'Searching deals',
  get_deal_details: 'Loading deal details',
  get_pipeline_summary: 'Fetching pipeline data',
  search_clients: 'Searching clients',
  get_dashboard_stats: 'Loading dashboard stats',
  search_purchase_orders: 'Searching purchase orders',
  get_po_details: 'Loading PO details',
  search_material_requests: 'Searching material requests',
  search_vendors: 'Searching vendors',
  get_sample_requests: 'Searching sample requests',
  get_overdue_followups: 'Checking overdue follow-ups',
  get_appointments_today: 'Loading today\'s appointments',
};

/** Turn MR-XXX-000000 / PO-000000 / GRN-000000 into links and strip markdown bold markers. */
function renderWithLinks(text: string, isUser: boolean): React.ReactNode {
  const clean = isUser ? text : text.replace(/\*\*/g, '');
  const re = /\b(MR-[A-Z]{2,4}-\d{6}|PO-\d{6}|GRN-\d{6})\b/g;
  const out: React.ReactNode[] = [];
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(clean)) !== null) {
    if (m.index > last) out.push(clean.slice(last, m.index));
    const code = m[1];
    const href = code.startsWith('MR-') ? `/mrs/${code}` : code.startsWith('PO-') ? `/pos/${code}` : `/grns/${code}`;
    out.push(<a key={i++} href={href} style={{ color: isUser ? '#fff' : undefined, textDecoration: 'underline' }}>{code}</a>);
    last = m.index + code.length;
  }
  if (last < clean.length) out.push(clean.slice(last));
  return out;
}

const ChatMessage: React.FC<{ message: ChatMessageData }> = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        marginBottom: 16,
      }}
    >
      {/* Avatar + Name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {!isUser && <RobotOutlined style={{ fontSize: 14, color: '#722ed1' }} />}
        <Text type="secondary" style={{ fontSize: 12 }}>
          {isUser ? 'You' : 'ERP Assistant'}
        </Text>
        {isUser && <UserOutlined style={{ fontSize: 14, color: '#1890ff' }} />}
      </div>

      {/* Tool Calls */}
      {message.toolCalls && message.toolCalls.length > 0 && (
        <div style={{ marginBottom: 8, maxWidth: '90%' }}>
          {message.toolCalls.map((tool, i) => (
            <Tag
              key={i}
              icon={
                tool.status === 'running' ? <LoadingOutlined spin /> :
                tool.status === 'error' ? <SearchOutlined style={{ color: '#ff4d4f' }} /> :
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
              }
              color={tool.status === 'running' ? 'processing' : tool.status === 'error' ? 'error' : 'success'}
              style={{ marginBottom: 4 }}
            >
              {TOOL_LABELS[tool.name] || tool.name}
              {tool.count !== undefined && ` (${tool.count} results)`}
            </Tag>
          ))}
        </div>
      )}

      {/* Message Bubble */}
      {message.content && (
        <div
          style={{
            maxWidth: '90%',
            padding: '10px 14px',
            borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
            background: isUser ? '#1890ff' : '#f5f5f5',
            color: isUser ? '#fff' : '#000',
            wordBreak: 'break-word',
          }}
        >
          <div
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
            }}
          >
            {renderWithLinks(message.content, isUser)}
          </div>
        </div>
      )}

      {/* Timestamp */}
      <Text type="secondary" style={{ fontSize: 11, marginTop: 2 }}>
        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Text>
    </div>
  );
};

export default ChatMessage;
